import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, RotateCcw, Sparkles, Loader2 } from "lucide-react";

/**
 * DocumentCropEditor — full-screen modal that lets the customer position four
 * corner handles on top of the uploaded document photo. Auto-detected corners
 * are the starting point; the customer drags each handle to hug the exact
 * document edges. On confirm, we return the corners (normalized 0-1) so the
 * caller can pass them through to the upload endpoint — the server then
 * crops+warps to EXACTLY that quad, so the printed output contains ONLY the
 * document, never the surrounding table / hand / lighting.
 *
 * Props:
 *   open              boolean
 *   file              File            — original image the customer just uploaded
 *   originalUrl       string          — data URL / object URL for the image
 *   autoDetectCorners () => Promise<{corners:[[x,y]...4]}>   — usually POST /shop/{slug}/detect-corners
 *   renderPreview     (corners) => Promise<Blob>             — usually POST /enhance-preview with corners
 *   onCancel          () => void
 *   onConfirm         (corners, previewUrl?) => void         — corners: normalized 0-1 array
 */
export default function DocumentCropEditor({
  open, file, originalUrl,
  autoDetectCorners, renderPreview,
  onCancel, onConfirm,
}) {
  const [corners, setCorners] = useState(null);  // [[x,y],...4] normalized
  const [detecting, setDetecting] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [dragIdx, setDragIdx] = useState(-1);
  const frameRef = useRef(null);
  const previewRefreshTimer = useRef(null);

  // Auto-detect on open ------------------------------------------------------
  useEffect(() => {
    if (!open || !file) return;
    setPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return null; });
    setCorners(null);
    setDetecting(true);
    autoDetectCorners()
      .then((res) => {
        if (res && Array.isArray(res.corners) && res.corners.length === 4) {
          setCorners(res.corners);
        } else {
          setCorners([[0.05, 0.05], [0.95, 0.05], [0.95, 0.95], [0.05, 0.95]]);
        }
      })
      .catch(() => {
        setCorners([[0.05, 0.05], [0.95, 0.05], [0.95, 0.95], [0.05, 0.95]]);
      })
      .finally(() => setDetecting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file]);

  // Debounced live preview refresh -------------------------------------------
  useEffect(() => {
    if (!open || !corners) return;
    if (previewRefreshTimer.current) clearTimeout(previewRefreshTimer.current);
    previewRefreshTimer.current = setTimeout(async () => {
      setRendering(true);
      try {
        const blob = await renderPreview(corners);
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch { /* ignore — customer can still adjust and confirm */ }
      finally { setRendering(false); }
    }, 320);
    return () => { if (previewRefreshTimer.current) clearTimeout(previewRefreshTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corners, open]);

  // Cleanup ------------------------------------------------------------------
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const onImgLoad = (e) => {
    setImgSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  };

  // Compute the on-screen pixel size of the image based on the frame's aspect
  // ratio + object-fit contain. We need this so the corner handles land on
  // the same visual point even when the frame letterboxes the image.
  const frameLayout = useCallback(() => {
    const frame = frameRef.current;
    if (!frame || !imgSize.w) return null;
    const fr = frame.getBoundingClientRect();
    const imgAspect = imgSize.w / imgSize.h;
    const frameAspect = fr.width / fr.height;
    let w, h, left = 0, top = 0;
    if (imgAspect > frameAspect) {
      w = fr.width; h = fr.width / imgAspect; top = (fr.height - h) / 2;
    } else {
      h = fr.height; w = fr.height * imgAspect; left = (fr.width - w) / 2;
    }
    return { fr, left, top, w, h };
  }, [imgSize]);

  const pointerXYToNormalised = (clientX, clientY) => {
    const layout = frameLayout();
    if (!layout) return null;
    const relX = (clientX - layout.fr.left - layout.left) / layout.w;
    const relY = (clientY - layout.fr.top - layout.top) / layout.h;
    return [Math.max(0, Math.min(1, relX)), Math.max(0, Math.min(1, relY))];
  };

  const startDrag = (idx) => (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragIdx(idx);
  };

  useEffect(() => {
    if (dragIdx < 0) return;
    const move = (e) => {
      const pt = e.touches ? e.touches[0] : e;
      const norm = pointerXYToNormalised(pt.clientX, pt.clientY);
      if (!norm) return;
      setCorners((cs) => {
        const next = cs.map((c, i) => (i === dragIdx ? norm : c));
        return next;
      });
    };
    const up = () => setDragIdx(-1);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragIdx, imgSize]);

  const resetToAuto = async () => {
    setDetecting(true);
    try {
      const res = await autoDetectCorners();
      if (res && Array.isArray(res.corners) && res.corners.length === 4) {
        setCorners(res.corners);
      }
    } finally { setDetecting(false); }
  };

  const handlePositions = useMemo(() => {
    const layout = frameLayout();
    if (!layout || !corners) return [];
    return corners.map(([nx, ny]) => ({
      left: layout.left + nx * layout.w,
      top: layout.top + ny * layout.h,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corners, imgSize]);

  // SVG polygon `points` attribute for the quad overlay.
  const polygonPoints = handlePositions.map((p) => `${p.left},${p.top}`).join(" ");

  const canConfirm = corners && corners.length === 4 && !detecting;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="doc-crop-editor">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="font-display flex items-center gap-2">
            Crop the document
            <span className="text-xs font-normal text-muted-foreground">
              Drag the 4 corners to hug your Aadhaar / PAN / RC / DL
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Position four handles to select the document area. Only the selected region will be printed.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-secondary/30">
          {/* Left: source with draggable corner handles */}
          <div
            ref={frameRef}
            className="relative bg-black rounded-lg overflow-hidden select-none"
            style={{ height: 460 }}
            data-testid="doc-crop-frame"
          >
            {originalUrl && (
              <img
                src={originalUrl}
                onLoad={onImgLoad}
                alt="source"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                draggable={false}
              />
            )}
            {/* Quad overlay */}
            {corners && polygonPoints && (
              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <polygon
                  points={polygonPoints}
                  fill="rgba(34,197,94,0.15)"
                  stroke="rgb(34,197,94)"
                  strokeWidth="2"
                />
              </svg>
            )}
            {/* Corner handles */}
            {handlePositions.map((p, i) => (
              <div
                key={i}
                onMouseDown={startDrag(i)}
                onTouchStart={startDrag(i)}
                className={`absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-brand-green shadow-lg cursor-move touch-none ${dragIdx === i ? "scale-125" : ""}`}
                style={{ left: p.left, top: p.top, transition: dragIdx === i ? "none" : "transform 120ms" }}
                data-testid={`doc-crop-handle-${i}`}
              />
            ))}
            {detecting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-sm">
                <Loader2 size={14} className="animate-spin mr-2" /> Detecting document edges…
              </div>
            )}
            <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-black/60 text-white text-[10px] uppercase tracking-widest font-semibold pointer-events-none">
              Original — drag corners
            </div>
          </div>

          {/* Right: live cropped preview */}
          <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: 460 }} data-testid="doc-crop-preview">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="preview"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                draggable={false}
              />
            )}
            {!previewUrl && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                {rendering ? (
                  <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Rendering…</span>
                ) : "Adjust corners to see the printed result"}
              </div>
            )}
            {rendering && previewUrl && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-black/60 text-white text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1">
                <Loader2 size={10} className="animate-spin" /> Updating
              </div>
            )}
            <div className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-brand-green text-white text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1 pointer-events-none">
              <Sparkles size={10} /> Will print
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-5 pt-3 gap-2 flex-wrap border-t bg-card">
          <Button variant="ghost" onClick={resetToAuto} disabled={detecting}
                  className="mr-auto text-muted-foreground hover:text-foreground"
                  data-testid="doc-crop-reset">
            <RotateCcw size={13} className="mr-1" /> Auto-detect edges
          </Button>
          <Button variant="outline" onClick={onCancel} data-testid="doc-crop-cancel">
            <X size={13} className="mr-1" /> Cancel
          </Button>
          <Button
            onClick={() => canConfirm && onConfirm(corners, previewUrl)}
            disabled={!canConfirm}
            className="bg-brand-green hover:bg-brand-green-hover text-white"
            data-testid="doc-crop-confirm"
          >
            <Check size={13} className="mr-1" /> Use this crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
